 
import React from "react";
import { PageLoader } from "../../../../../../../components/PageLoader/PageLoader";
import { StandadCard } from "../../../../../../../components/Cards";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { LoadState, MODULE_IDS } from "../../../../../../../constants/enums";
import { useDispatchWrapper } from "../../../../../../../hooks";
import { useFormik } from "formik";
import {
  Box,
  Checkbox,
  Divider,
  Grid,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomShrinkTextField,
  CustomTextField,
} from "../../../../../../../components/formsComponents";

import produce from "immer";
import {
  addAutomobileScheduleAAsync,
  clearAutomobileScheduleState,
  fetchAutomobileScheduleAAsync,
} from "../../../../../../../redux/automobileSchedule/automobileScheduleActions";
import { RoleBasedCustomButton } from "../../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { AutoComplete } from "../../../../../../../components/AutoCompleteSearches/AutoComplete";
import {
  COMMON_STATUS,
  csioOtherCoverages,
  AUTOMOBILE_CSIO_COVERAGE,
} from "../../../../../../../constants/constants";
import { useDefaultQuote } from "../../../../../../../hooks/useDefaultQuote";
import { IAutomobileScheduleA } from "../../../../../../../redux/automobileSchedule/automobileSchedule.types";
import { FieldSet } from "../../../../../../../components/FieldSet/FieldSet";
import { useAutomobilePolicyVehcileInfo } from "../../hooks/useAutomobileVehicleInfo";
import { PageContainer } from "../../../../../../../components/container/PageContainer";

// Canonical coverage ordering (same order used by the Coverage application
// screen / CSIO). Saved schedule records can come back in a different order,
// so we normalize to this sequence. Codes not listed here (and blank "other"
// rows) keep their original relative position at the end.
const COVERAGE_CODE_ORDER = [
  "TPBI",
  "TPPD",
  "AB",
  "MRB",
  "CIMRB",
  "CATIM",
  "WIB",
  "NEB",
  "CGB",
  "CGCAT",
  "LEE",
  "EXV",
  "HHM",
  "HHCAT",
  "DPI",
  "DEA",
  "FNB",
  "DCB",
  "IDB",
  "UA",
  "TPDC",
  "SP",
  "CMP",
  "COL",
  "AP",
  "44R",
  "20",
  "47R",
  "27",
  "23A",
];

const sortCoverageDetails = (
  details: IAutomobileScheduleA["coverage_details"]
): IAutomobileScheduleA["coverage_details"] => {
  if (!Array.isArray(details)) {
    return details;
  }
  const rank = (code: string) => {
    const idx = COVERAGE_CODE_ORDER.indexOf(code);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  };
  // Stable sort: preserve original order for equal ranks (unknown codes and
  // discount/other rows stay in their backend order at the end).
  return details
    .map((item, i) => ({ item, i }))
    .sort((a, b) => {
      const diff = rank(a.item.code) - rank(b.item.code);
      return diff !== 0 ? diff : a.i - b.i;
    })
    .map(({ item }) => item);
};

interface IAutomobileScheduleAProps {
  customerId?: number;
  customerPolicyId?: number;
  incrementalNumber?: number;
  readOnly?: boolean;
  fromRightPanel?: boolean;
  staticStatusValue?: string;
  created_from?: string;
  onSaveSuccess: (data: IAutomobileScheduleA) => void;
  initialData?: IAutomobileScheduleA;
  disableApiCalls?: boolean;
  vehicleNumber?: number;
  isVehicleEnabled?: boolean;
  isPolicyChange?: boolean;
}

export const AutomobileScheduleA: React.FC<IAutomobileScheduleAProps> = (
  props
) => {
  const {
    customerId,
    customerPolicyId,
    incrementalNumber,
    onSaveSuccess,
    fromRightPanel = false,
    readOnly = false,
    staticStatusValue,
    created_from,
    initialData,
    disableApiCalls = false,
    vehicleNumber,
    isVehicleEnabled = true,
    isPolicyChange = false,
  } = props;

  const { data, loading } = useSelector(
    (storeState: IStoreState) => storeState.automobile.schedule.scheduleA
  );

  const isUpdate = incrementalNumber ? true : false;

  const dispatch = useDispatchWrapper();
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [manual, setManual] = React.useState(false);
  const { coverageList, fetchDefaultCoverageByCode } = useDefaultQuote();

  const { vehcileDropdown, vehiclesList } =
    useAutomobilePolicyVehcileInfo(customerPolicyId);

  const { values, errors, handleChange, handleSubmit, setValues } = useFormik({
    initialValues: initialData
      ? {
          ...initialData,
          coverage_details: sortCoverageDetails(initialData.coverage_details),
        }
      : data,
    validate: (values) => {
      const errors: any = {};
      if (isVehicleEnabled && !values.vehicle_number) {
        errors.vehicle_number = "Vehicle is required.";
      }
      return errors;
    },
    onSubmit: (values) => {
      if (disableApiCalls) {
        onSaveSuccess(values);
      } else {
        if (customerPolicyId && customerId) {
          let statusValue = values.status;
          if (staticStatusValue) {
            statusValue = staticStatusValue;
          }
          setSaveLoading(true);
          dispatch(
            addAutomobileScheduleAAsync(
              {
                ...values,
                customer_policy_id: customerPolicyId,
                status: statusValue,
                created_from: created_from || "Coverage",
              },
              (isSuccess, data) => {
                if (isSuccess && data) {
                  onSaveSuccess(data);
                  window.scrollTo(0, 0);
                }
                setSaveLoading(false);
              }
            )
          );
        }
      }
    },
  });

  const handleTextChange =
    (index: number, key: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_details[index][key as "key"] = e.target.value;
      });
      setValues(newValues);
    };

  const handleSelectChange =
    (index: number, key: string) => (selectedOption: any) => {
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_details[index][key as "key"] = selectedOption
          ? selectedOption
          : "";
      });
      setValues(newValues);
    };

  const handleCoverageChange = async (e: SelectChangeEvent<unknown>) => {
    const coverageCode = e.target.value as string;

    const CoverageInfo = await fetchDefaultCoverageByCode(coverageCode);

    if (CoverageInfo) {
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_name = coverageCode;
        draftValues.coverage_details = CoverageInfo.coverage_details;
      });
      setValues(newValues);
    }
  };

  const handleCheckboxChange =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      const newValues = produce(values, (draftValues) => {
        draftValues.coverage_details[index].isDeductible = isChecked;
      });
      setValues(newValues);
    };

  React.useEffect(() => {
    if (initialData && disableApiCalls) {
      return;
    }
    setValues({
      ...data,
      coverage_details: sortCoverageDetails(data.coverage_details),
    });
  }, [data]);

  React.useEffect(() => {
    if (
      customerPolicyId &&
      incrementalNumber &&
      vehicleNumber &&
      !disableApiCalls
    ) {
      dispatch(
        fetchAutomobileScheduleAAsync(
          customerPolicyId,
          incrementalNumber,
          vehicleNumber
        )
      );
    }
  }, []);

  React.useEffect(() => {
    return () => {
      dispatch(clearAutomobileScheduleState());
    };
  }, []);

  const total = React.useMemo(() => {
    return values.coverage_details.reduce((item, currentValue) => {
      return item + (Number(currentValue.premimum) || 0);
    }, 0);
  }, [values.coverage_details]);

  const totalTwo = React.useMemo(() => {
    return values.coverage_details.reduce((item, currentValue) => {
      return item + (Number(currentValue.description) || 0);
    }, 0);
  }, [values.coverage_details]);

  const defaultCoverageList = React.useMemo(() => {
    return coverageList.map((item) => {
      return {
        label: `${item.default_coverage_name || "--"} ( ${
          item.default_coverage_code || "N/A"
        } 
        )`,
        value: item.default_coverage_code || "",
      };
    });
  }, [coverageList]);

  const handleVinNumber = async (value: string | number) => {
    const vehicleNumber = value as number;
    const vinNumber = vehiclesList.find(
      (x) => x.incremental_number === Number(vehicleNumber)
    );

    if (vinNumber) {
      const newValues = produce(values, (draftValues) => {
        draftValues.vehicle_number = vehicleNumber;
        draftValues.vin = vinNumber?.vin || "";
      });
      setValues(newValues);
    } else {
      const newValues = produce(values, (draftValues) => {
        draftValues.vehicle_number = vehicleNumber;
        draftValues.vin = (vehicleNumber || "").toString();
      });
      setValues(newValues);
    }
  };

  return (
     <PageContainer
            title={
             isUpdate
                ? "Edit Schedule Summary"
                : "Create Schedule Summary"
            }
            description="Manage summary ."
          >
    <PageLoader loading={loading === LoadState.InProgress}>
      <StandadCard
        sx={{
          "& > .MuiBox-root:first-of-type > .MuiTypography-root:first-of-type": {
            fontSize: "1rem",
          },
        }}
        renderWithoutCard={fromRightPanel}
        heading={!fromRightPanel ? "Vehicle Coverage Summary" : ""}
        rightHeading={
          !incrementalNumber && (
            <FieldSet disabled={readOnly}>
              <Stack
                direction={"row"}
                spacing={3}
                width={"100%"}
                alignItems={"center"}
              >
                <Typography
                  display={"block"}
                  variant="h6"
                  fontWeight={"bold"}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Default Coverage{" "}
                </Typography>
                <ControlledCustomSelect
                  sx={{ width: "300px" }}
                  variant="outlined"
                  size="small"
                  type="string"
                  name="coverage_name"
                  value={values.coverage_name}
                  onChange={handleCoverageChange}
                  displayEmpty
                  fullWidth
                  placeholder="Select one"
                  options={defaultCoverageList}
                ></ControlledCustomSelect>
              </Stack>
            </FieldSet>
          )
        }
      >
        <form onSubmit={handleSubmit}>
          <FieldSet disabled={readOnly}>
            <>
              <Grid container sx={{ mt: 0 }} spacing={0.5}>
                <Grid item xs={12} md={2}>
                  <CustomFormLabel
                    fontWeight={600}
                    variant="h6"
                    textAlign={"center"}
                    sx={{ mt: 0 }}
                  >
                    Coverage #
                  </CustomFormLabel>
                  <CustomTextField
                    name="coverage_number"
                    variant="outlined"
                    size="small"
                    type="text"
                    fullWidth
                    value={values.coverage_number}
                    onChange={handleChange}
                    disabled={!isPolicyChange}
                  />
                </Grid>
                {isVehicleEnabled && (
                  // <Grid item xs={6} md={isPolicyChange ? 6 : 4}>
                  //   <CustomFormLabel
                  //     fontWeight={600}
                  //     variant="h4"
                  //     textAlign={"center"}
                  //   >
                  //     Vehicle Number
                  //   </CustomFormLabel>
                  //   <ExtendableSelect
                  //     value={values.vehicle_number}
                  //     onChange={handleVinNumber}
                  //     fullWidth
                  //     placeholder="Select one"
                  //     options={vehcileDropdown}
                  //     errorMessage={errors.vehicle_number}
                  //     error={errors.vehicle_number ? true : false}
                  //   ></ExtendableSelect>
                  // </Grid>
                  <Grid item xs={12} md={2}>
                    <CustomFormLabel
                      fontWeight={600}
                      variant="h6"
                      textAlign={"center"}
                      sx={{ mt: 0 }}
                    >
                      Manual Vehicle
                    </CustomFormLabel>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      <Checkbox
                        checked={manual}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setManual(isChecked);
                        }}
                      />
                    
                    </Stack>
                  </Grid>
                )}
                {isVehicleEnabled && (
                  <Grid item xs={12} md={6}>
                    <CustomFormLabel
                      fontWeight={600}
                      variant="h6"
                      textAlign={"center"}
                      sx={{ mt: 0 }}
                    >
                      Vehicle Number
                    </CustomFormLabel>
                    {manual ? (
                      <CustomTextField
                        fullWidth
                        value={values.vehicle_number}
                        name="vehicle_number"
                        type="text"
                        onChange={(e) => handleVinNumber(e.target.value)}
                        error={errors.vehicle_number ? true : false}
                        helperText={errors.vehicle_number}
                      />
                    ) : (
                      <ControlledCustomSelect
                        variant="outlined"
                        size="small"
                        type="string"
                        name="vehicle_number"
                        value={values.vehicle_number ? String(values.vehicle_number) : ""}
                        onChange={(e) => handleVinNumber(Number(e.target.value))}
                        displayEmpty
                        fullWidth
                        placeholder="Select one"
                        options={vehcileDropdown}
                        error={errors.vehicle_number ? true : false}
                        helperText={errors.vehicle_number}
                      />
                    )}
                  </Grid>
                )}
                {!staticStatusValue && (
                  <Grid item xs={12} md={2}>
                    <CustomFormLabel
                      fontWeight={600}
                      variant="h6"
                      textAlign={"center"}
                      sx={{ mt: 0 }}
                    >
                      Status
                    </CustomFormLabel>
                    <ControlledCustomSelect
                      fullWidth
                      value={values.status}
                      name="status"
                      onChange={handleChange}
                      placeholder="Select one"
                      displayEmpty
                      options={COMMON_STATUS}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={12}>
                  <TableContainer
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                    }}
                  >
                    <Table
                      size="small"
                      aria-label="collapsible table"
                      sx={{
                        whiteSpace: {
                          xs: "nowrap",
                          sm: "nowrap",
                        },
                        "& .MuiTableCell-root": {
                          py: 0.25,
                          px: 0.5,
                        },
                        "& tbody .MuiTableRow-root": {
                          backgroundColor: (theme) =>
                            theme.palette.common.white,
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow
                          sx={{
                            "& .MuiTableCell-root": {
                              backgroundColor: (theme) =>
                                theme.palette.primary.main,
                              color: (theme) =>
                                theme.palette.primary.contrastText,
                            },
                          }}
                        >
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Section
                            </Typography>
                          </TableCell>
                          <TableCell align="center" colSpan={3}>
                            <Typography fontWeight={600} variant="h6">
                              Automobile
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            "& .MuiTableCell-root": {
                              backgroundColor: (theme) =>
                                theme.palette.success.main,
                              color: (theme) =>
                                theme.palette.success.contrastText,
                            },
                          }}
                        >
                          
                           <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Liability
                            </Typography>
                          </TableCell> 
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Limit
                            </Typography>
                          </TableCell>
                          {/* <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Deductible
                            </Typography>
                          </TableCell> */}
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Premium
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography fontWeight={600} variant="h6">
                              Occassional Driver Premium
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.coverage_details.map((item, index) => {
                          const showDeductibleOnly = [
                            "Specified Perils (excluding Collision or Upset)",
                            "Comprehensive (excluding Collision or Upset)",
                            "Collision or Upset",
                            "All Perils",
                            "Direct Compensation-Property Damage",
                          ].includes(item.label);

                          // Some rows come back with an empty label; fall back
                          // to the CSIO code map so the coverage name shows.
                          const csioLabel = item.code
                            ? AUTOMOBILE_CSIO_COVERAGE[`csio:${item.code}`] || ""
                            : "";
                          const resolvedLabel = (item.label || csioLabel)
                            .replace(/Policy Change Forms \(Name & No\.\)\s*/gi, "")
                            .replace(/^\s*[-\u2010-\u2015]\s*/, "")
                            .trimStart();

                          // Standard CSIO coverages (a code that maps to a known
                          // label) should render as a plain label like the
                          // application coverage screen, not as an editable
                          // dropdown. Only genuine "other" rows (no known
                          // coverage) keep the dropdown selector.
                          const isKnownCoverage = Boolean(csioLabel);
                          const showAsLabel = !item.isTypeOther || isKnownCoverage;

                          // Bold a few key coverage headings. Normalize the
                          // displayed text (strip trailing punctuation, unify
                          // dash characters, collapse spaces) so matches work
                          // regardless of en-dash vs hyphen or trailing commas.
                          const normalizeLabel = (value: string) =>
                            (value || "")
                              .replace(/[\u2010-\u2015]/g, "-")
                              .replace(/[\s,]+$/g, "")
                              .replace(/\s+/g, " ")
                              .trim()
                              .toLowerCase();
                          const isBoldLabel = [
                            "Accident Benefits (Standard Benefits)",
                            "Uninsured Automobile",
                            "Direct Compensation-Property Damage",
                          ]
                            .map(normalizeLabel)
                            .includes(normalizeLabel(resolvedLabel));

                          return (
                            <React.Fragment key={item.key}>
                              {(normalizeLabel(resolvedLabel).includes(
                                "family protection coverage"
                              ) ||
                                normalizeLabel(resolvedLabel).includes(
                                  "opcf 44r"
                                )) && (
                                <TableRow
                                  sx={{
                                    "&.MuiTableRow-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                    },
                                    "& .MuiTableCell-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                      color: (theme) =>
                                        theme.palette.success.contrastText,
                                    },
                                  }}
                                >
                                  <TableCell>
                                    <Typography fontWeight={600} variant="h6">
                                      Policy Change Forms (Name & No.)
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Deductible/Limit
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Premium
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Occassional Driver Premium
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}

                              {item.label ===
                                "Specified Perils (excluding Collision or Upset)" && (
                                <TableRow
                                  sx={{
                                    "&.MuiTableRow-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                    },
                                    "& .MuiTableCell-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                      color: (theme) =>
                                        theme.palette.success.contrastText,
                                    },
                                  }}
                                >
                                  <TableCell>
                                    <Typography fontWeight={600} variant="h6">
                                      LOSS OR DAMAGE
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Deductible
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Premium
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography fontWeight={600} variant="h6">
                                      Occassional Driver Premium
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}

                              <TableRow
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell sx={{ minWidth: 300}}>
                                  {showAsLabel ? (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      spacing={0.5}
                                      sx={{ width: "100%" }}
                                    >
                                      <Typography
                                        fontWeight={isBoldLabel ? 700 : 400}
                                        sx={{
                                          wordBreak: "break-word",
                                          whiteSpace: "normal",
                                        }}
                                      >
                                        {resolvedLabel}
                                      </Typography>

                                      {item.label ===
                                        "Family Protection Coverage - OPCF 44R" && (
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                        >
                                          <Typography>
                                            is deductible?
                                          </Typography>
                                          <Checkbox
                                            checked={item.isDeductible}
                                            onChange={handleCheckboxChange(
                                              index
                                            )}
                                          />
                                        </Stack>
                                      )}
                                    </Stack>
                                  ) : (
                                    <Stack
                                      direction="row"
                                      gap={2}
                                      sx={{ width: "100%", display: "flex" }}
                                    >
                                      <AutoComplete
                                        fullWidth
                                        value={resolvedLabel}
                                        placeholder="Select one"
                                        options={
                                          resolvedLabel &&
                                          !csioOtherCoverages.some(
                                            (opt) =>
                                              opt.value.toLowerCase() ===
                                              resolvedLabel.toLowerCase()
                                          )
                                            ? [
                                                {
                                                  label: resolvedLabel,
                                                  value: resolvedLabel,
                                                },
                                                ...csioOtherCoverages,
                                              ]
                                            : csioOtherCoverages
                                        }
                                        onChange={handleSelectChange(
                                          index,
                                          "label"
                                        )}
                                      />

                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                      >
                                        <Typography>is deductible?</Typography>
                                        <Checkbox
                                          checked={item.isDeductible}
                                          onChange={handleCheckboxChange(index)}
                                        />
                                      </Stack>
                                    </Stack>
                                  )}
                                </TableCell>
                                <TableCell >
                                  <CustomShrinkTextField
                                    label={
                                      (showDeductibleOnly || item.isDeductible
                                        ? item.deductible
                                        : item.limit)
                                        ? ""
                                        : showDeductibleOnly || item.isDeductible
                                        ? "Deductible"
                                        : "Limit"
                                    }
                                    value={
                                      showDeductibleOnly || item.isDeductible
                                        ? item.deductible
                                        : item.limit
                                    }
                                    onChange={handleTextChange(
                                      index,
                                      showDeductibleOnly || item.isDeductible
                                        ? "deductible"
                                        : "limit"
                                    )}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                   
                                  />
                                </TableCell>
                                <TableCell >
                                  <CustomShrinkTextField
                                    label={item.premimum ? "" : "Premium"}
                                    value={item.premimum}
                                    onChange={handleTextChange(
                                      index,
                                      "premimum"
                                    )}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    
                                  />
                                </TableCell>
                                <TableCell >
                                  <CustomShrinkTextField
                                    label={item.description ? "" : "Driver"}
                                    value={item.description}
                                    onChange={handleTextChange(
                                      index,
                                      "description"
                                    )}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                   
                                  />
                                </TableCell>
                              </TableRow>

                              {item.label ===
                                "Accident Benefits (Standard Benefits)" && (
                                <TableRow
                                  sx={{
                                    "&.MuiTableRow-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                    },
                                    "& .MuiTableCell-root": {
                                      backgroundColor: (theme) =>
                                        theme.palette.success.main,
                                      color: (theme) =>
                                        theme.palette.success.contrastText,
                                    },
                                  }}
                                >
                                  <TableCell colSpan={5}>
                                    <Typography fontWeight={600} variant="h6">
                                      Optional Increased Accident Benefits
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>

              <Divider sx={{ mt: 1.5, mb: 1.5 }} />
              <Grid
                container
                spacing={2}
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.grey[100],
                }}
              >
                <Grid item xs={9}>
                  <Typography variant="h5" fontWeight={600}>
                    Total Premium
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="h5" fontWeight={600}>
                    $ {total}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography variant="h5" fontWeight={600}>
                    $ {totalTwo}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <CustomFormLabel
                      fontWeight={600}
                      variant="h4"
                    >
                      Remarks
                    </CustomFormLabel>
                    <CustomTextField
                      name="remarks"
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                      multiline
                      rows={3}
                      value={values.remarks || ""}
                      onChange={handleChange}
                      placeholder="Enter remarks"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mt: 2.5 }}>
                <RoleBasedCustomButton
                  moduleKey={MODULE_IDS.AUTOMOBILE}
                  sx={{ width: "15%" }}
                  disabled={saveLoading}
                  variant="contained"
                  type="submit"
                >
                  Save
                </RoleBasedCustomButton>
              </Box>
            </>
          </FieldSet>
        </form>
      </StandadCard>
    </PageLoader>
    </PageContainer>
  );
};
